import { ref } from 'vue'

import { defineStore } from 'pinia'

import { useAuthStore } from './auth'
import { useRatingStore } from './rating'

export const SUMMARY_POLL_INTERVAL = 4000

export const SUMMARY_POLL_SLOW_INTERVAL = 60000

/**
 * How the poll cadence backs off the longer a summary stays pending, longest threshold first.
 */
export const SUMMARY_POLL_BACKOFF = [
    { afterMs: 10 * 60 * 1000, intervalMs: SUMMARY_POLL_SLOW_INTERVAL },
    { afterMs: 2 * 60 * 1000, intervalMs: 15000 },
]

/**
 * The client a notes module is for. Its `segment` is the API path below /api/v1; an unsaved client (the create view)
 * has none, so nothing calls the API for it.
 * @param {number|string|null} id
 * @returns {{ id: number|string|null, segment: string|null }}
 */
export function notesSubject(id) {
    const persisted = id !== null && id !== undefined && id !== '' && Number(id) !== 0

    return { id: persisted ? id : null, segment: persisted ? `clients/${id}` : null }
}

/**
 * The record id in a timeline event key such as "note:42" or "feedback:42".
 */
function idFromKey(key) {
    return key.split(':')[1]
}

/**
 * The delay before the next summary poll: SUMMARY_POLL_INTERVAL at first, backing off by SUMMARY_POLL_BACKOFF
 * as the polling time grows, and the slow cadence straight away once the server reports that the pending
 * generation failed (the server's `failed` flag). A failed generation stays pending by design.
 * @param {number} elapsedMs
 * @param {{ failed?: boolean }} [options]
 * @returns {number}
 */
function summaryPollDelay(elapsedMs, { failed = false } = {}) {
    if (failed) {
        return SUMMARY_POLL_SLOW_INTERVAL
    }

    return SUMMARY_POLL_BACKOFF.find((step) => elapsedMs >= step.afterMs)?.intervalMs ?? SUMMARY_POLL_INTERVAL
}

function documentHidden() {
    return typeof document !== 'undefined' && document.hidden === true
}

export const useNotesStore = defineStore('notes', () => {
    const authStore = useAuthStore()
    const ratingStore = useRatingStore()

    const subject = ref(null)

    const events = ref([])
    const loadingTimeline = ref(false)
    const savingNote = ref(false)
    const deletingNoteKey = ref(null)
    const updatingNoteKey = ref(null)

    const summary = ref(null)
    const pending = ref(false)
    const generating = ref(false)
    const failed = ref(false)
    const loadingSummary = ref(false)
    const summaryLoaded = ref(false)

    const notAvailable = ref(false)

    let pollTimeout = null
    let pollingSegment = null
    let pollGeneration = 0
    let pollElapsedMs = 0
    let pollWaitingForVisibility = false

    function currentSegment() {
        return subject.value?.segment ?? null
    }

    function isActiveSegment(segment) {
        return segment !== null && currentSegment() === segment
    }

    /**
     * Points the store at a subject. Switching to a different subject resets all state.
     */
    function setSubject(nextSubject) {
        if (nextSubject?.segment && nextSubject.segment === currentSegment()) {
            subject.value = nextSubject
            return
        }
        reset()
        subject.value = nextSubject ?? null
    }

    /**
     * Load failures stay quiet when the module is disabled server-side (403), otherwise they notify.
     */
    function handleLoadError(err, segment) {
        if (err?.response?.status === 403) {
            if (isActiveSegment(segment)) {
                notAvailable.value = true
            }
            return
        }
        authStore.handleError(err)
    }

    //
    // Timeline
    //

    async function loadTimeline() {
        const segment = currentSegment()
        loadingTimeline.value = true

        try {
            const response = await axios.get(`/api/v1/${segment}/timeline`)
            const data = response.data ?? []
            if (isActiveSegment(segment)) {
                events.value = data
            }
            return data
        } catch (err) {
            handleLoadError(err, segment)
            throw { error: err }
        } finally {
            if (isActiveSegment(segment)) {
                loadingTimeline.value = false
            }
        }
    }

    async function addNote(text) {
        const segment = currentSegment()
        savingNote.value = true

        try {
            const response = await axios.post(`/api/v1/${segment}/notes`, { body: text })
            const created = response.data
            if (isActiveSegment(segment)) {
                events.value = [created, ...events.value]
                markSummaryPending()
            }
            return created
        } catch (err) {
            authStore.handleError(err)
            throw { error: err }
        } finally {
            savingNote.value = false
        }
    }

    /**
     * The summary is marked pending only when the server recorded an edit, seen as a new `updated_at` (unchanged text
     * is a server-side no-op).
     */
    async function updateNote(event, text) {
        const segment = currentSegment()
        updatingNoteKey.value = event.key

        try {
            const response = await axios.patch(`/api/v1/${segment}/notes/${idFromKey(event.key)}`, { body: text })
            const updated = response.data
            if (isActiveSegment(segment)) {
                events.value = events.value.map((item) => (item.key === updated.key ? { ...item, ...updated } : item))
                if (updated.updated_at !== (event.updated_at ?? null)) {
                    markSummaryPending()
                }
            }
            return updated
        } catch (err) {
            authStore.handleError(err)
            throw { error: err }
        } finally {
            updatingNoteKey.value = null
        }
    }

    /**
     * The new feedback event comes from a timeline reload, awaited so the event is listed when this resolves; the
     * JobRatingCreated listener requests the summary server-side.
     */
    async function addFeedback(jobId, rating, text) {
        const segment = currentSegment()
        savingNote.value = true

        try {
            const response = await ratingStore.store(jobId, { status: rating, note_content: text })
            ratingStore.applySavedRating(response?.data)
            if (isActiveSegment(segment)) {
                markSummaryPending()
                await loadTimeline().catch(() => {})
            }
            return response?.data ?? null
        } catch (err) {
            authStore.handleError(err?.error ?? err)
            throw err?.error ? err : { error: err }
        } finally {
            savingNote.value = false
        }
    }

    async function updateFeedback(event, rating, text) {
        const segment = currentSegment()
        const ratingId = idFromKey(event.key)
        const jobId = event.data.job_id
        updatingNoteKey.value = event.key

        try {
            const response = await axios.patch(`/api/v1/jobs/${jobId}/ratings/${ratingId}`, { status: rating, note_content: text })
            ratingStore.applySavedRating(response?.data)
            if (isActiveSegment(segment)) {
                events.value = events.value.map((item) => (item.key === event.key ? { ...item, text, data: { ...item.data, rating } } : item))
                loadTimeline().catch(() => {})
                markSummaryPending()
            }
            return response?.data ?? null
        } catch (err) {
            authStore.handleError(err)
            throw { error: err }
        } finally {
            updatingNoteKey.value = null
        }
    }

    async function deleteFeedback(event) {
        const segment = currentSegment()
        const ratingId = idFromKey(event.key)
        const jobId = event.data.job_id
        deletingNoteKey.value = event.key

        try {
            await axios.delete(`/api/v1/jobs/${jobId}/ratings/${ratingId}`)
            ratingStore.applyRemovedRating(jobId, ratingId)
            if (isActiveSegment(segment)) {
                events.value = events.value.filter((item) => item.key !== event.key)
                markSummaryPending()
            }
            return event
        } catch (err) {
            authStore.handleError(err)
            throw { error: err }
        } finally {
            deletingNoteKey.value = null
        }
    }

    async function deleteNote(event) {
        const segment = currentSegment()
        deletingNoteKey.value = event.key

        try {
            await axios.delete(`/api/v1/${segment}/notes/${idFromKey(event.key)}`)
            if (isActiveSegment(segment)) {
                events.value = events.value.filter((item) => item.key !== event.key)
                markSummaryPending()
            }
            return event
        } catch (err) {
            authStore.handleError(err)
            throw { error: err }
        } finally {
            deletingNoteKey.value = null
        }
    }

    //
    // Summary
    //

    async function fetchSummary(segment) {
        const response = await axios.get(`/api/v1/${segment}/summary`)

        return { ...response.data }
    }

    function applySummaryResponse(data) {
        if (!summaryLoaded.value || !data.pending) {
            summary.value = data.summary
        }
        pending.value = data.pending
        generating.value = Boolean(data.pending && data.generating)
        failed.value = Boolean(data.pending && data.failed)
        summaryLoaded.value = true
    }

    async function loadSummary() {
        const segment = currentSegment()
        loadingSummary.value = true

        try {
            const data = await fetchSummary(segment)
            if (isActiveSegment(segment)) {
                applySummaryResponse(data)
                if (data.pending) {
                    startPolling(firstPollDelay(data))
                }
            }
            return data
        } catch (err) {
            handleLoadError(err, segment)
            throw { error: err }
        } finally {
            loadingSummary.value = false
        }
    }

    /**
     * A client profile change that the timeline shows (disposition) also requests a summary server-side:
     * reload the timeline and show the summary as pending. Does nothing without a saved subject.
     */
    function clientProfileChanged() {
        if (currentSegment() === null) {
            return
        }
        loadTimeline().catch(() => {})
        markSummaryPending()
    }

    /**
     * A note change requests a summary server-side; show the pending state right away and poll
     * until the server reports nothing pending, then swap in the new summary. The change restarts the
     * poll chain so it is back at the fast cadence.
     */
    function markSummaryPending() {
        pending.value = true
        stopPolling()
        startPolling()
    }

    /**
     * The server's expected turnaround (`retry_after_seconds`) delays the first poll after a load,
     * so a freshly requested summary is not polled before it can be ready; a recorded failure starts
     * at the slow cadence; later polls follow summaryPollDelay.
     */
    function firstPollDelay(data) {
        if (data.failed) {
            return summaryPollDelay(0, { failed: true })
        }

        return data.pending && data.retry_after_seconds !== null ? data.retry_after_seconds * 1000 : SUMMARY_POLL_INTERVAL
    }

    function startPolling(firstDelay = SUMMARY_POLL_INTERVAL) {
        const segment = currentSegment()
        if (segment !== null && pollingSegment === segment) {
            return
        }
        stopPolling()
        pollingSegment = segment
        pollElapsedMs = 0
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', onVisibilityChange)
        }
        scheduleNextPoll(firstDelay)
    }

    function scheduleNextPoll(delay) {
        const generation = pollGeneration
        pollElapsedMs += delay
        pollTimeout = setTimeout(() => pollOnce(generation), delay)
    }

    /**
     * Polls while pending with no give-up: a failed generation stays pending by design until the next change
     * re-requests it, so the cadence backs off instead. Each chain has a generation, and a response for an older
     * chain is dropped, so a quick subject switch never leaves two chains running. A poll due while the tab
     * is hidden waits for it to become visible. Transient request errors keep polling; a 403 stops it.
     */
    async function pollOnce(generation) {
        const segment = pollingSegment
        if (generation !== pollGeneration || segment === null) {
            return
        }
        pollTimeout = null
        if (documentHidden()) {
            pollWaitingForVisibility = true
            return
        }

        let serverFailed = false
        try {
            const data = await fetchSummary(segment)
            if (generation !== pollGeneration) {
                return
            }
            applySummaryResponse(data)
            if (!data.pending) {
                stopPolling()
                return
            }
            serverFailed = Boolean(data.failed)
        } catch (err) {
            if (generation !== pollGeneration) {
                return
            }
            if (err?.response?.status === 403) {
                stopPolling()
                handleLoadError(err, segment)
                return
            }
        }
        scheduleNextPoll(summaryPollDelay(pollElapsedMs, { failed: serverFailed }))
    }

    function onVisibilityChange() {
        if (pollWaitingForVisibility && !documentHidden()) {
            pollWaitingForVisibility = false
            pollOnce(pollGeneration)
        }
    }

    function stopPolling() {
        clearTimeout(pollTimeout)
        pollTimeout = null
        pollingSegment = null
        pollGeneration += 1
        pollWaitingForVisibility = false
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', onVisibilityChange)
        }
    }

    async function submitFeedback(summaryId, { rating, comment } = {}) {
        const segment = currentSegment()
        const payload = { rating }
        if (comment !== undefined) {
            payload.comment = comment
        }

        try {
            const response = await axios.put(`/api/v1/${segment}/summaries/${summaryId}/feedback`, payload)
            const feedback = response.data ?? null
            applyFeedback(summaryId, feedback)
            return feedback
        } catch (err) {
            authStore.handleError(err)
            throw { error: err }
        }
    }

    function applyFeedback(summaryId, feedback) {
        const current = summary.value
        if (!feedback || !current || current.id !== summaryId) {
            return
        }
        summary.value = { ...current, my_feedback: [feedback] }
    }

    function reset() {
        stopPolling()
        subject.value = null
        events.value = []
        loadingTimeline.value = false
        savingNote.value = false
        deletingNoteKey.value = null
        updatingNoteKey.value = null
        summary.value = null
        pending.value = false
        generating.value = false
        failed.value = false
        loadingSummary.value = false
        summaryLoaded.value = false
        notAvailable.value = false
    }

    return {
        subject,
        events,
        loadingTimeline,
        savingNote,
        deletingNoteKey,
        updatingNoteKey,
        summary,
        pending,
        generating,
        failed,
        loadingSummary,
        notAvailable,
        setSubject,
        loadTimeline,
        clientProfileChanged,
        addNote,
        updateNote,
        deleteNote,
        addFeedback,
        updateFeedback,
        deleteFeedback,
        loadSummary,
        stopPolling,
        submitFeedback,
        reset,
    }
})
